type DotLottieModule = typeof import("@lottiefiles/dotlottie-web");

type IdleDeadline = { didTimeout: boolean; timeRemaining: () => number };
type IdleCallback = (deadline: IdleDeadline) => void;

const hasWindow = typeof window !== "undefined";

const scheduleIdleCallback: ((callback: IdleCallback) => number) | null = hasWindow
  ? ((window as typeof window & { requestIdleCallback?: (cb: IdleCallback) => number }).requestIdleCallback ??
      ((callback: IdleCallback) => window.setTimeout(() => callback({ didTimeout: true, timeRemaining: () => 0 }), 1)))
  : null;

const cancelIdleCallback: ((handle: number) => void) | null = hasWindow
  ? ((window as typeof window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback ??
      ((handle: number) => window.clearTimeout(handle)))
  : null;

let loadPromise: Promise<DotLottieModule> | null = null;
let idleHandle: number | null = null;

function beginImport(): Promise<DotLottieModule> | null {
  if (!hasWindow) {
    return null;
  }

  if (!loadPromise) {
    loadPromise = import("@lottiefiles/dotlottie-web").then(
      (module) => module,
      (error) => {
        loadPromise = null;
        throw error;
      }
    );
  }

  return loadPromise;
}

export function preloadDotLottie(options: { immediate?: boolean } = {}): void {
  if (!hasWindow || loadPromise) {
    return;
  }

  const triggerLoad = () => {
    idleHandle = null;
    beginImport()?.catch((error) => {
      if (import.meta.env.DEV) {
        console.error("Failed to preload dotlottie module", error);
      }
    });
  };

  if (options.immediate || !scheduleIdleCallback) {
    triggerLoad();
    return;
  }

  if (idleHandle !== null) {
    return;
  }

  idleHandle = scheduleIdleCallback(triggerLoad);
}

export function loadDotLottie(): Promise<DotLottieModule> {
  if (!hasWindow) {
    return Promise.resolve({} as DotLottieModule);
  }

  if (idleHandle !== null && cancelIdleCallback) {
    cancelIdleCallback(idleHandle);
    idleHandle = null;
  }

  const promise = beginImport();
  if (!promise) {
    return Promise.resolve({} as DotLottieModule);
  }

  return promise;
}
